import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import { useVendorExperienceById } from "../../custom-hooks/queries";

export default function ExperienceDetails() {
  const navigate = useNavigate();
  const { experienceId } = useParams();

  const {
    data: responseData,
    isLoading,
    isError,
  } = useVendorExperienceById(experienceId);

  const experienceData = responseData?.data || responseData;

  const title = experienceData?.title;
  const description = experienceData?.description;
  const location = experienceData?.location;
  const price = experienceData?.price;
  const category = experienceData?.category;
  const difficulty = experienceData?.difficulty;
  const notes = experienceData?.importantNotes;

  const isPrivate = experienceData?.isPrivate;
  const maxGuestCount = experienceData?.maxGuestCount;
  const baseDurationMinutes = experienceData?.baseDurationMinutes;
  const baseGroupSize = experienceData?.baseGroupSize;
  const bufferMinutes = experienceData?.bufferMinutes;

  const availability = experienceData?.availability || [];
  const escalationRules = experienceData?.escalationRules || [];
  const availabilityOverrides = experienceData?.availabilityOverrides || [];

  const addOns = experienceData?.addsOn || [];
  const images = experienceData?.images?.map((url) => ({ preview: url })) || [];

  const formatTime = (timeStr) => {
    if (!timeStr) return "—";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  const formatDate = (date) => {
    return format(new Date(date), "MMM dd, yyyy");
  };

  const formatDateRange = (start, end) => {
    return `${formatDate(start)} → ${formatDate(end)}`;
  };

  if (isLoading) {
    return (
      <div className="py-6 px-1">
        <PageHeader
          title="Experience Details"
          description="View and manage your experience information"
        />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1FADC6]"></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-6 px-1">
        <PageHeader
          title="Experience Details"
          description="View and manage your experience information"
        />
        <div className="flex justify-center items-center py-20 text-red-500">
          Failed to load experience details.
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-1">
      <PageHeader
        title="Experience Details"
        description="View and manage your experience information"
      />

      <div className="bg-white rounded-[12px] border border-[#D9D9D9] shadow-sm p-6 sm:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-8">
          {/* ================= LEFT ================= */}
          <div>
            <h3 className="text-[18px] font-semibold text-gray-900 mb-4">
              Details
            </h3>

            <div className="relative p-5">
              <div className="absolute left-0 top-4 h-[85%] w-[3px] bg-[#00B8C8] rounded-full" />

              <ul className="ml-4 text-[16px] text-gray-800 space-y-3">
                <li>
                  <b>→ Title:</b> {title || "—"}
                </li>
                <li>
                  <b>→ Description:</b> {description || "—"}
                </li>
                <li>
                  <b>→ Category:</b> {category || "—"}
                </li>
                <li>
                  <b>→ Difficulty:</b> {difficulty || "—"}
                </li>
                <li>
                  <b>→ Price:</b> ${price || 0}
                </li>
                <li>
                  <b>→ Location:</b> {location?.fullAddress || "—"}
                </li>

                <li>
                  <b>→ Type:</b>{" "}
                  {isPrivate ? "Private Experience" : "Public Experience"}
                </li>

                {!isPrivate && (
                  <li>
                    <b>→ Max Guests:</b> {maxGuestCount || "—"}
                  </li>
                )}

                <li>
                  <b>→ Base Duration:</b> {baseDurationMinutes} minutes
                </li>

                <li>
                  <b>→ Base Group Size:</b> {baseGroupSize}
                </li>

                <li>
                  <b>→ Buffer Time:</b> {bufferMinutes} minutes
                </li>

                {notes && (
                  <li>
                    <b>→ Notes:</b> {notes}
                  </li>
                )}

                {addOns?.length > 0 && (
                  <li>
                    <b>→ Add-ons:</b>
                    <ul className="ml-4 mt-2 space-y-1">
                      {addOns.map((a, i) => (
                        <li key={i}>
                          • {a.name} — ${a.price}
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </div>

            {/* ================= AVAILABILITY ================= */}
            <div className="mt-8">
              <h4 className="text-[16px] font-semibold mb-3">
                Availability Rules
              </h4>

              {availability?.length ? (
                <div className="space-y-4">
                  {availability.map((rule, index) => (
                    <div
                      key={index}
                      className="border rounded-xl p-4 shadow-sm"
                    >
                      <div className="text-sm font-semibold mb-2">
                        {formatDateRange(rule.startDate, rule.endDate)}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {rule.openDays?.map((day) => (
                          <span
                            key={day}
                            className="px-3 py-1 rounded-full text-xs bg-[#1FADC6] text-white"
                          >
                            {day}
                          </span>
                        ))}
                      </div>

                      <div className="text-sm text-gray-700">
                        {formatTime(rule.startTime)} –{" "}
                        {formatTime(rule.endTime)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No availability configured.
                </p>
              )}
            </div>

            {/* ================= OVERRIDES ================= */}
            <div className="mt-8">
              <h4 className="text-[16px] font-semibold mb-3">
                Availability Overrides
              </h4>

              {availabilityOverrides.length ? (
                <div className="space-y-3">
                  {availabilityOverrides.map((o) => (
                    <div
                      key={o.experienceAvailabilityOverrideId}
                      className="border rounded-xl p-4 shadow-sm"
                    >
                      <div className="font-semibold text-sm mb-2">
                        {formatDate(o.date)}
                      </div>

                      {o.type === "FullDayClosed" && (
                        <div className="text-red-600 text-sm">
                          Full Day Closed
                        </div>
                      )}

                      {o.type === "CustomWindow" && (
                        <div className="text-sm">
                          Custom Hours: {formatTime(o.startTime)} –{" "}
                          {formatTime(o.endTime)}
                        </div>
                      )}

                      {o.type === "BlockedRange" && (
                        <div className="text-sm text-purple-600">
                          Partial Closure (Blocked Range)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No overrides applied.</p>
              )}
            </div>

            {/* ================= ESCALATION ================= */}
            <div className="mt-8">
              <h4 className="text-[16px] font-semibold mb-3">
                Escalation Rules
              </h4>

              {escalationRules?.length ? (
                <div className="border rounded-xl divide-y shadow-sm">
                  {escalationRules.map((row, index) => (
                    <div key={index} className="p-4">
                      <div className="text-sm font-semibold">
                        Guests: {row.minGuests} – {row.maxGuests}
                      </div>
                      <div className="text-sm text-gray-700">
                        Duration: {row.durationMinutes} minutes
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  No escalation rules defined.
                </p>
              )}
            </div>
          </div>

          {/* ================= RIGHT (IMAGES UNCHANGED) ================= */}
          <div className="space-y-5">
            {images?.length ? (
              images.map((img, i) => (
                <img
                  key={i}
                  src={img.preview}
                  className="w-full h-[260px] object-cover rounded-2xl"
                  alt=""
                />
              ))
            ) : (
              <div className="border rounded-xl p-8 text-center text-gray-500">
                No images uploaded
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-8">
        <button
          onClick={() =>
            navigate(`/vendor/experiences/new?experienceId=${experienceId}`)
          }
          className="h-11 px-10 rounded-lg bg-[#00B8C8] text-white font-semibold text-sm hover:bg-[#009FB0]"
        >
          Edit Experience
        </button>
      </div>
    </div>
  );
}
